import { createLogic } from 'redux-logic';
import { stopSubmit, setSubmitFailed } from 'redux-form';
import history from '../utils/history';
import {
  GET_CREDIT_CARDS_PENDING,
  PATH_CREDIT_CARDS,
  ADD_CREDIT_CARD_PENDING,
  UPDATE_CREDIT_CARD_PENDING,
  DELETE_CREDIT_CARD_PENDING,
  DELETE_CREDIT_CARD_CONFIRM
} from '../constants';
import {
  getCreditCardsSuccess,
  getCreditCardsError,
  setBannerState,
  addCreditCardSuccess,
  addCreditCardError,
  deleteCreditCardSuccess,
  deleteCreditCardError,
  deleteCreditCardPending,
  toggleModal,
  toggleForm,
  triggerModal,
  toggleProcessedItem,
  updateCreditCardSuccess,
  updateCreditCardError,
  setAddressSuggestions,
  getCreditCardsPending,
} from '../actions/AppActions';
import intl from '../actions/lang/en.json';
import { cardType } from '../utils/creditCard';

export const getCreditCardsLogic = createLogic({
  type: GET_CREDIT_CARDS_PENDING,
  latest: true,
  process({ httpClient }, dispatch, done) {
    httpClient(dispatch).get('/credit-cards')
      .then(({ data }) => {
        const creditCards = data?.map(creditCard => ({
          ...creditCard,
          cardType: cardType(creditCard?.token),
        }));
        dispatch(getCreditCardsSuccess({ data: creditCards }));
        done();
      })
      .catch(err => {
        const message = intl.Errors.PaymentMethods.Get_CreditCards_Error;
        const bannerState = {
          data: { type: 'error', message }
        };
        dispatch(setBannerState(bannerState));
        dispatch(getCreditCardsError(err));
        done();
      });
  }
});

export const addCreditCardLogic = createLogic({
  type: ADD_CREDIT_CARD_PENDING,
  latest: true,
  process({ httpClient, action: { payload } }, dispatch, done) {
    const form = 'CreditCard';
    const cardData = expirationDateToInt(payload);
    httpClient(dispatch).post('/credit-cards', cardData)
      .then(res => {
        dispatch(stopSubmit(form));
        dispatch(addCreditCardSuccess(res.data));
        history.push(PATH_CREDIT_CARDS);
        dispatch(getCreditCardsPending());
        done();
      })
      .catch(error => {
        const addressSuggestions = error?.response?.data?.addressSuggestions;
        const isAddressSuggestionError = error?.response?.status === 406 && addressSuggestions?.length;

        if (isAddressSuggestionError) {
          dispatch(setAddressSuggestions(addressSuggestions));
        }

        const errorMessage = isAddressSuggestionError
          ? intl.Errors.ShippingAddresses.Address_Suggestion_Error
          : intl.Errors.PaymentMethods.Add_Credit_Card_Error;

        dispatch(stopSubmit(form, { _error: errorMessage }));
        dispatch(setSubmitFailed(form));
        dispatch(addCreditCardError({ err: error?.response?.data ?? error, status: error?.response?.status }));
        dispatch(setBannerState({ data: { type: 'error', errorMessage } }));
        done();
      });
  }
});

export const updateCreditCardLogic = createLogic({
  type: UPDATE_CREDIT_CARD_PENDING,
  latest: true,
  process({ httpClient, action }, dispatch, done) {
    const { payload } = action;
    const { id, form } = payload;
    const card = {
      address: {
        active: payload?.address?.active,
        category: payload?.address?.category,
        city: payload?.address?.city,
        countryCode: payload?.address?.countryCode,
        countrySubdivision: payload?.address?.countrySubdivision,
        defaultAddress: payload?.address?.defaultAddress,
        firstName: payload?.address?.firstName,
        id: payload?.address?.id,
        lastName: payload?.address?.lastname,
        line1: payload?.address?.line1,
        phoneNumber: payload?.address?.phoneNumber,
        postalCode: payload?.address?.postalCode,
        type: payload?.address?.type,
      },
      cardHolderName: payload?.cardHolderName,
      cardType: payload?.cardType,
      displayName: payload?.displayName,
      expirationMonth: payload?.expirationMonth,
      expirationYear: payload?.expirationYear,
      id: payload?.id,
      token: payload?.token,
    };
    httpClient(dispatch).put('/credit-cards', expirationDateToInt(card))
      .then(res => {
        history.push(PATH_CREDIT_CARDS);
        dispatch(stopSubmit(form));
        dispatch(updateCreditCardSuccess({ existingId: id, updatedCreditCard: res.data }));
        dispatch(getCreditCardsPending());
        done();
      })
      .catch(error => {
        const addressSuggestions = error?.response?.data?.addressSuggestions;
        const isAddressSuggestionError = error?.response?.status === 406 && addressSuggestions?.length;

        if (isAddressSuggestionError) {
          dispatch(setAddressSuggestions(addressSuggestions));
        }

        const errorMessage = isAddressSuggestionError
          ? intl.Errors.ShippingAddresses.Address_Suggestion_Error
          : intl.Errors.PaymentMethods.Add_Credit_Card_Error;

        dispatch(stopSubmit(form, { _error: errorMessage }));
        dispatch(setSubmitFailed(form));
        dispatch(updateCreditCardError({ err: error?.response?.data ?? error, status: error?.response?.status }));
        dispatch(setBannerState({ data: { type: 'error', errorMessage } }));
        done();
      });
  }
});

export const removeCreditCardLogic = createLogic({
  type: DELETE_CREDIT_CARD_PENDING,
  latest: true,
  process({ httpClient, action }, dispatch, done) {
    const id = action.payload.id;
    httpClient(dispatch).delete(`/credit-cards/${id}`)
      .then(() => {
        history.push(PATH_CREDIT_CARDS);
        dispatch(deleteCreditCardSuccess(id));
        dispatch(getCreditCardsPending());
        done();
      })
      .catch(error => {
        dispatch(deleteCreditCardError({ err: error?.response?.data ?? error, status: error?.response?.status }));
        dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.PaymentMethods.Delete_Card_Error } }));
        done();
      });
  }
});

export const deleteCreditCardConfirmLogic = createLogic({
  type: DELETE_CREDIT_CARD_CONFIRM,
  latest: true,
  processOptions: {
    dispatchMultiple: true,
  },
  process({ action }, dispatch) {
    const { id, displayName, cardSubscriptions } = action.payload;
    const modalProps = {
      show: true,
      subscriptions: cardSubscriptions,
      message: `${intl.Errors.PaymentMethods.Delete_Warning_Credit_Card} ${displayName}?`,
      okLabel: 'Yes',
      okDataHook: 'deleteCreditCardYes.button',
      cancelLabel: 'No',
      cancelDataHook: 'deleteCreditCardNo.button',
      buttonOrder: 'rtl',
      okClick: () => {
        dispatch(toggleProcessedItem(id));
        dispatch(deleteCreditCardPending({ id }));
        dispatch(toggleModal());
      },
      cancelClick: () => {
        dispatch(toggleModal());
        history.push(PATH_CREDIT_CARDS);
      }
    };
    dispatch(toggleForm(null));
    dispatch(triggerModal(modalProps));
  }
});

export function expirationDateToInt(payload) {
  const toNumber = (val) => val && ~~val;
  const { expirationMonth, expirationYear } = payload;
  return {
    ...payload,
    expirationMonth: expirationMonth && toNumber(expirationMonth),
    expirationYear: expirationYear && toNumber(expirationYear)
  };
}

export default [
  getCreditCardsLogic,
  addCreditCardLogic,
  deleteCreditCardConfirmLogic,
  removeCreditCardLogic,
  updateCreditCardLogic,
];
